import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { apiClient } from "@/lib/api";
import { applyBrand } from "@/lib/brand";
import type { ActingAsProfile, BrandConfig, PartnerSummary, Profile } from "@tilt/admin-sdk";

import { PartnerScopeContext, type PartnerScopeValue } from "./partner-scope-context";

// Namespaced to match preferences.ts convention.
const SCOPE_STORAGE_KEY = "finterra.admin.scope";

function readStoredScope(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(SCOPE_STORAGE_KEY);
  } catch {
    // Safari private mode / quota / storage denied - fall back to session.
    return null;
  }
}

function writeStoredScope(id: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (id === null) {
      window.localStorage.removeItem(SCOPE_STORAGE_KEY);
    } else {
      window.localStorage.setItem(SCOPE_STORAGE_KEY, id);
    }
  } catch {
    // Storage unavailable - not fatal, session still works in-memory.
  }
}

/**
 * Loads the caller's profile, derives the list of partners they can
 * switch into, remembers the last pick in localStorage, and exposes
 * the PartnerScope context to descendants.
 *
 * While the profile is loading we render a minimal placeholder - every
 * page downstream assumes scope is populated, so gating here keeps the
 * rest of the tree simple.
 */
export function PartnerScopeProvider(props: { children: ReactNode }): JSX.Element {
  // Two related profile queries running in parallel:
  //
  // - /profile returns the actor's identity + roles. Never swaps on
  //   impersonation (audit-attribution reads from this response).
  //   The Profile page ("my account") + the brand header source from
  //   this one so account UI always shows the real caller.
  //
  // - /profile/acting-as returns the TARGET's identity + roles when
  //   an impersonation session is active, or the actor's otherwise.
  //   Every role-aware helper (sidebar, button gates, scope picker)
  //   reads from this so impersonation visually swaps perspective.
  //
  // Both queries cache independently; the UI picks the right one
  // per use-case via ``getQueryData(["profile"])`` (actor) or
  // ``getQueryData(["profile","acting-as"])`` (role view).
  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => apiClient.getProfile(),
    retry: false,
    staleTime: 60_000,
  });
  const actingAs = useQuery({
    queryKey: ["profile", "acting-as"],
    queryFn: () => apiClient.getActingAsProfile(),
    retry: false,
    staleTime: 60_000,
  });

  // Scope picker + role gates read from the acting-as profile so an
  // impersonated partner_admin sees only their own accessible
  // partners (not the actor's wider set). When no impersonation is
  // active, acting-as returns the actor's roles anyway.
  //
  // Deploy-ordering fallback: if the admin-api is running an older
  // build that doesn't have /profile/acting-as yet (or the endpoint
  // 5xx's transiently), fall back to the actor's /profile as the
  // role source. Impersonation visually won't swap - the sidebar +
  // gates render against the actor's roles - but the app otherwise
  // works. The cache is also primed so useRoleProfile consumers see
  // a coherent payload under ["profile","acting-as"].
  const roleSource: ActingAsProfile | Profile | undefined = actingAs.isError
    ? profile.data
    : actingAs.data;
  const available = useMemo<PartnerSummary[]>(() => {
    if (!roleSource) return [];
    const seen = new Map<string, PartnerSummary>();
    // primary partner is always switchable.
    seen.set(roleSource.primary_partner.id, roleSource.primary_partner);
    for (const r of roleSource.roles) {
      if (seen.has(r.partner_id)) continue;
      seen.set(r.partner_id, {
        id: r.partner_id,
        name: r.partner_name,
        path: r.partner_path,
        // RoleGrant does not carry partner_type; the switcher only
        // needs name+path for display, so default to "partner".
        partner_type: "partner",
      });
    }
    return [...seen.values()].sort((a, b) => a.path.localeCompare(b.path));
  }, [roleSource]);

  const [scopeId, setScopeId] = useState<string | null>(readStoredScope);

  // When the profile loads, pick a default scope if the stored one
  // is missing or no longer accessible (role was revoked, partner
  // deleted, etc). Persist the fallback so we don't repeatedly start
  // with an invalid id on every page load.
  useEffect(() => {
    if (available.length === 0) return;
    const exists = scopeId !== null && available.some((t) => t.id === scopeId);
    if (!exists) {
      const fallback = available[0]?.id ?? null;
      setScopeId(fallback);
      writeStoredScope(fallback);
    }
  }, [available, scopeId]);

  const scope = useMemo<PartnerSummary | null>(() => {
    if (!scopeId) return available[0] ?? null;
    return available.find((t) => t.id === scopeId) ?? available[0] ?? null;
  }, [scopeId, available]);

  const setScope = useCallback((partner: PartnerSummary) => {
    setScopeId(partner.id);
    writeStoredScope(partner.id);
  }, []);

  // Brand cascade: location override wins, then org, then partner.
  // ``product_name`` is the only field where org/loc commonly diverge
  // from partner today - so we MERGE the configs (partner is base,
  // org overlays, loc overlays last) rather than swap them. Logo +
  // colors stay inherited unless an org/loc explicitly overrides.
  // Empty objects fall through cleanly via the spread.
  const brand = useMemo<BrandConfig>(
    () => ({
      ...(profile.data?.primary_partner.brand_config ?? {}),
      ...(profile.data?.primary_organization?.brand_config ?? {}),
      ...(profile.data?.primary_location?.brand_config ?? {}),
    }),
    [
      profile.data?.primary_partner.brand_config,
      profile.data?.primary_organization?.brand_config,
      profile.data?.primary_location?.brand_config,
    ],
  );

  // Apply brand CSS vars whenever the primary_partner's brand_config
  // changes. Scope switching does NOT re-skin - brand follows the
  // caller's home workspace, not the subtree they're poking into.
  useEffect(() => {
    applyBrand(brand);
  }, [brand]);

  // Block render until both queries have settled. /profile/acting-as
  // determines the available partner list (scope picker + role gates).
  // If we unblock after /profile alone, acting-as is still in-flight,
  // available=[]/scope=null, and the error screen flashes briefly before
  // acting-as resolves. Waiting for both avoids that flash with minimal
  // added latency since the two requests fire in parallel.
  //
  // Exception: if acting-as has already errored we fall back to /profile
  // as the role source (handled above), so we should not keep spinning.
  if (profile.isLoading || (actingAs.isLoading && !actingAs.isError)) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Loading profile…
      </div>
    );
  }

  // Only hard-fail when /profile itself is broken - the actor
  // identity is load-bearing (audit banner, account page, role
  // fallback). An acting-as outage is handled above by falling
  // back to /profile as the role source, so the app renders a
  // functional actor-only view instead of the fatal screen.
  if (profile.isError || !scope) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          Unable to load admin profile.{" "}
          {profile.error instanceof Error ? profile.error.message : "Unknown error"}
        </div>
      </div>
    );
  }

  const value: PartnerScopeValue = { scope, available, setScope, brand };
  return (
    <PartnerScopeContext.Provider value={value}>{props.children}</PartnerScopeContext.Provider>
  );
}
